import { createTheme } from "@mui/material/styles";

/* =============================================================================
   Design tokens & selectable themes.

   Every palette below is a COMPLETE token set — same keys, different values.
   The active palette is picked at boot from localStorage and copied into the
   exported `tokens` object, so the many module-level style constants across the
   app (tableKit's th/td, dataGridSx, StatusChip, …) bake in the right colours
   the moment they are imported. Switching themes therefore persists the choice
   and reloads the page; see `setThemeMode`.

   The same palette is also written out as `--sk-*` CSS custom properties by
   `applyCssVars`, which is what re-skins the legacy Bootstrap / react-data-table
   screens (see skin.css). theme.js is the single source of truth for colour —
   the `:root` block in skin.css is only a pre-JS fallback.
   ========================================================================== */

export const THEME_STORAGE_KEY = "admin.themeMode";
export const DEFAULT_THEME = "midnight";

// Inline SVG chevron for Bootstrap's .form-select, tinted per theme.
const selectArrow = (strokeHex) =>
  `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23${strokeHex}' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`;

/* ---------------------------------- Midnight -------------------------------
   The original SpinSaga Command Center palette — purple / teal on deep navy. */
const midnight = {
  id: "midnight",
  label: "Midnight",
  hint: "Purple & teal on deep navy",
  scheme: "dark",

  // surfaces
  bg: "#0C0E1C",
  bg2: "#101327",
  surface: "#161A33", // panel
  surface2: "#1C2140", // panel2
  raised: "#232A4E",

  // lines
  border: "#2A3056", // line
  border2: "#353C68", // line2
  borderHover: "rgba(255,255,255,0.18)",

  // brand
  primary: "#8B6DF0", // purple
  primaryDark: "#6A4BD6",
  secondary: "#23D6A0", // green
  accent: "#F0B43D", // gold

  // semantic
  danger: "#F25C66",
  amber: "#F5A623",
  blue: "#5BA8F5",
  pink: "#E86FB0",

  // text
  text: "#E8EAF6",
  textStrong: "#FFFFFF",
  textDim: "#8990B8",
  textDim2: "#5E648C",
  onPrimary: "#FFFFFF",
  onAccent: "#0C0E1C", // text sitting on a bright accent fill
  onMint: "#06281F",
  link: "#23D6A0",
  linkHover: "#8B6DF0",

  // composites
  cardBg: "linear-gradient(180deg,#161A33,#101327)",
  bodyBg:
    "radial-gradient(1100px 600px at 78% -8%, rgba(139,109,240,0.10), transparent 60%)," +
    "radial-gradient(900px 500px at -5% 12%, rgba(35,214,160,0.06), transparent 55%)," +
    "#0C0E1C",
  appBar: "rgba(11,14,26,0.72)",
  brandGrad: "linear-gradient(135deg,#8B6DF0,#5B3FB0)",
  gradPrimary: "linear-gradient(135deg,#8B6DF0,#6A4BD6)",
  navActiveBg: "linear-gradient(90deg, rgba(139,109,240,0.20), rgba(139,109,240,0.04))",
  navActiveBorder: "rgba(139,109,240,0.40)",

  // states
  hover: "rgba(139,109,240,0.08)",
  hoverStrong: "rgba(139,109,240,0.18)",
  selected: "rgba(139,109,240,0.13)",
  focusRing: "rgba(139,109,240,0.25)",
  stripe: "rgba(255,255,255,0.025)",
  ghostBtn: "rgba(255,255,255,0.06)",
  ghostBtnDisabled: "rgba(255,255,255,0.03)",
  dimDisabled: "rgba(154,163,192,0.40)",
  shadow: "0 8px 28px rgba(0,0,0,0.45)",
  shadowPrimary: "0 8px 24px rgba(139,109,240,0.35)",

  // chrome
  scrollThumb: "#353C68",
  scrollThumbHover: "#3F477A",
  scrollTrack: "#0C0E1C",
  selectionBg: "rgba(139,109,240,0.40)",
  invert: "invert(1) grayscale(1) brightness(1.6)", // Bootstrap .btn-close
  chevron: "invert(0.85) brightness(1.4)", // accordion chevron
  selectArrow: selectArrow("8990b8"),
};

/* ----------------------------------- White ---------------------------------
   Light theme: near-black chrome on white, greyscale surfaces. Status colours
   stay (muted) so approve / pending / reject remain distinguishable. */
const white = {
  id: "white",
  label: "White",
  hint: "Light, greyscale chrome",
  scheme: "light",

  bg: "#F4F6F8",
  bg2: "#FAFBFC",
  surface: "#FFFFFF",
  surface2: "#F1F3F6",
  raised: "#E7EAEF",

  border: "#E2E5EA",
  border2: "#C8CDD6",
  borderHover: "rgba(0,0,0,0.28)",

  primary: "#111111",
  primaryDark: "#000000",
  secondary: "#1F7A55",
  accent: "#8A6D1F",

  danger: "#B3261E",
  amber: "#8A6D1F",
  blue: "#1F5FA8",
  pink: "#93336B",

  text: "#14161A",
  textStrong: "#000000",
  textDim: "#5B6270",
  textDim2: "#878E9B",
  onPrimary: "#FFFFFF",
  onAccent: "#FFFFFF",
  onMint: "#FFFFFF",
  link: "#111111",
  linkHover: "#4A4A4A",

  cardBg: "linear-gradient(180deg,#FFFFFF,#FAFBFC)",
  bodyBg: "#F4F6F8",
  appBar: "rgba(255,255,255,0.82)",
  brandGrad: "linear-gradient(135deg,#2B2B2B,#000000)",
  gradPrimary: "linear-gradient(135deg,#2B2B2B,#000000)",
  navActiveBg: "linear-gradient(90deg, rgba(0,0,0,0.08), rgba(0,0,0,0.02))",
  navActiveBorder: "rgba(0,0,0,0.22)",

  hover: "rgba(0,0,0,0.04)",
  hoverStrong: "rgba(0,0,0,0.10)",
  selected: "rgba(0,0,0,0.07)",
  focusRing: "rgba(0,0,0,0.18)",
  stripe: "rgba(0,0,0,0.02)",
  ghostBtn: "rgba(0,0,0,0.05)",
  ghostBtnDisabled: "rgba(0,0,0,0.03)",
  dimDisabled: "rgba(91,98,112,0.40)",
  shadow: "0 8px 28px rgba(16,20,28,0.10)",
  shadowPrimary: "0 6px 18px rgba(0,0,0,0.18)",

  scrollThumb: "#C8CDD6",
  scrollThumbHover: "#AAB1BD",
  scrollTrack: "#F4F6F8",
  selectionBg: "rgba(0,0,0,0.15)",
  invert: "none",
  chevron: "none",
  selectArrow: selectArrow("5b6270"),
};

export const palettes = { midnight, white };

// Ordered list for the theme picker, with swatches (bg / primary / accent).
export const THEMES = Object.values(palettes).map((p) => ({
  id: p.id,
  label: p.label,
  hint: p.hint,
  swatch: [p.bg, p.primary, p.secondary],
}));

/* ------------------------------ mode plumbing ---------------------------- */

export function getThemeMode() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (saved && palettes[saved]) return saved;
    // A theme that no longer exists (e.g. the retired "black") — drop the
    // stale entry so the picker and storage agree on the default.
    if (saved) localStorage.removeItem(THEME_STORAGE_KEY);
  } catch (e) {
    /* storage blocked (private mode / embedded) — fall through to default */
  }
  return DEFAULT_THEME;
}

// Map the palette onto the --sk-* custom properties skin.css / index.css read.
// Written to <html> inline so they win over the :root fallback in skin.css.
export function applyCssVars(palette) {
  const root = document.documentElement;
  const vars = {
    "--sk-bg": palette.bg,
    "--sk-bg2": palette.bg2,
    "--sk-surface": palette.surface,
    "--sk-surface2": palette.surface2,
    "--sk-raised": palette.raised,
    "--sk-border": palette.border,
    "--sk-border2": palette.border2,
    "--sk-border-hover": palette.borderHover,
    "--sk-primary": palette.primary,
    "--sk-mint": palette.secondary,
    "--sk-gold": palette.accent,
    "--sk-danger": palette.danger,
    "--sk-text": palette.text,
    "--sk-text-strong": palette.textStrong,
    "--sk-dim": palette.textDim,
    "--sk-dim2": palette.textDim2,
    "--sk-dim-disabled": palette.dimDisabled,
    "--sk-on-primary": palette.onPrimary,
    "--sk-on-mint": palette.onMint,
    "--sk-link": palette.link,
    "--sk-link-hover": palette.linkHover,
    "--sk-card": palette.cardBg,
    "--sk-body-bg": palette.bodyBg,
    "--sk-hover": palette.hover,
    "--sk-hover-strong": palette.hoverStrong,
    "--sk-focus": palette.focusRing,
    "--sk-stripe": palette.stripe,
    "--sk-ghost-btn": palette.ghostBtn,
    "--sk-ghost-btn-disabled": palette.ghostBtnDisabled,
    "--sk-shadow": palette.shadow,
    "--sk-shadow-primary": palette.shadowPrimary,
    "--sk-scroll-thumb": palette.scrollThumb,
    "--sk-scroll-thumb-hover": palette.scrollThumbHover,
    "--sk-scroll-track": palette.scrollTrack,
    "--sk-selection": palette.selectionBg,
    "--sk-invert": palette.invert,
    "--sk-chevron": palette.chevron,
    "--sk-select-arrow": palette.selectArrow,
  };
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.setAttribute("data-theme", palette.id);
  root.style.colorScheme = palette.scheme;
}

const activeMode = getThemeMode();
const activePalette = palettes[activeMode];

/** Tokens for the active theme. Import these anywhere a colour is needed. */
export const tokens = { ...activePalette };

if (typeof document !== "undefined") applyCssVars(activePalette);

/**
 * Re-save the active theme after a blanket `localStorage.clear()` (logout, or
 * the global 401 handler). The theme is a device preference, not session data.
 */
export function persistThemeMode() {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, activeMode);
  } catch (e) {
    /* storage blocked — nothing to restore */
  }
}

/**
 * Persist a theme and reload. The reload is deliberate: dozens of style objects
 * across the app are evaluated at module scope, so a full boot is what makes
 * every screen — including the legacy Bootstrap ones and the recharts graphs —
 * agree on the new palette.
 */
export function setThemeMode(mode) {
  if (!palettes[mode] || mode === getThemeMode()) return;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch (e) {
    /* storage blocked — apply for this session only */
  }
  applyCssVars(palettes[mode]);
  window.location.reload();
}

/* --------------------------------- MUI theme ------------------------------ */

export function createAppTheme(mode) {
  const p = palettes[mode] || palettes[DEFAULT_THEME];

  return createTheme({
    palette: {
      mode: p.scheme,
      background: { default: p.bg, paper: p.surface },
      primary: { main: p.primary, contrastText: p.onPrimary },
      secondary: { main: p.secondary },
      success: { main: p.secondary },
      warning: { main: p.accent },
      error: { main: p.danger },
      text: { primary: p.text, secondary: p.textDim },
      divider: p.border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      // Space Grotesk for display/headings (SpinSaga reference)
      h4: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, letterSpacing: -0.5 },
      h5: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, letterSpacing: -0.3 },
      h6: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 },
      subtitle1: { fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${p.border}`,
            backgroundColor: p.surface,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 9 },
          containedPrimary: {
            backgroundImage: p.gradPrimary,
            color: p.onPrimary,
            boxShadow: p.shadowPrimary,
            // MUI's .Mui-disabled only resets background-color, so the
            // gradient survives and swallows the muted label. Clear it.
            "&.Mui-disabled": {
              backgroundImage: "none",
              backgroundColor: p.ghostBtn,
              color: p.dimDisabled,
              boxShadow: "none",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: p.cardBg,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: p.border,
            fontSize: 12.5,
            padding: "11px 12px",
          },
          head: {
            color: p.textDim,
            fontSize: 10.5,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 700,
            whiteSpace: "nowrap",
            backgroundColor: "transparent",
          },
          stickyHeader: {
            backgroundColor: p.surface,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
        },
      },
    },
  });
}

const theme = createAppTheme(activeMode);

export default theme;
